import React, { useState } from 'react';
import {
    Card,
    CardContent,
    CardMedia,
    Typography,
    Button,
    Box,
    IconButton,
    CardActions,
    Avatar,
    Stack,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AMENITY_LABELS } from '../../constants/amenities';
import ImageGallery from '../common/ImageGallery';

const GENDER_LABELS = {
    male: '男性',
    female: '女性',
    other: 'その他'
};

const RoomManageCard = ({ room, onUpdate, onDelete, onStatusUpdate, onCardClick }) => {
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const navigate = useNavigate();

    const isInactive = room.status === 'inactive';

    const handleEdit = () => {
        navigate(`/rooms/edit/${room._id}`);
    };

    const handleDeleteClick = (e) => {
        e.stopPropagation();
        setOpenDeleteDialog(true);
    };

    const handleConfirmDelete = () => {
        onDelete(room._id);
        setOpenDeleteDialog(false);
    };

    const getStatusButtonLabel = () => {
        return room.status === 'active' ? '募集を一時停止する' : '募集を再開する';
    };

    const handleStatusToggle = () => {
        const newStatus = room.status === 'active' ? 'inactive' : 'active';
        onStatusUpdate(room._id, newStatus);
    };

    const formatPrice = (price) => {
        if (!price) return '価格未設定';
        return `¥${price.toLocaleString()}/月`;
    };

    return (
        <>
            <Card
                sx={{
                    position: 'relative',
                    cursor: 'pointer',
                    '&:hover': {
                        boxShadow: 6
                    },
                    opacity: isInactive ? 0.7 : 1,
                    filter: isInactive ? 'grayscale(40%)' : 'none'
                }}
                onClick={onCardClick}
            >
                <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar
                                src={room.owner?.profile?.profileImage}
                                alt={room.owner?.profile?.nickname}
                                sx={{ width: 24, height: 24 }}
                            />
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <Typography variant="body2">
                                    {room.owner?.profile?.nickname || '未設定'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    • {room.owner?.profile?.age}歳
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    • {GENDER_LABELS[room.owner?.profile?.gender] || '未設定'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    • {new Date(room.createdAt).toLocaleDateString()}
                                </Typography>
                            </Stack>
                        </Stack>
                        <Chip
                            label={isInactive ? '募集停止中' : '募集中'}
                            color={isInactive ? 'default' : 'success'}
                            size="small"
                            sx={{
                                fontWeight: 'bold',
                                minWidth: '80px'
                            }}
                        />
                    </Stack>
                </Box>

                <Box sx={{ position: 'relative', height: 200 }}>
                    <ImageGallery
                        images={room.images}
                        height={200}
                        singleImage={true}
                    />
                </Box>
                <CardContent onClick={(e) => e.stopPropagation()}>
                    <Typography variant="h6" color="primary">
                        {formatPrice(room.rentamount)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {room.location.prefecture} {room.location.city}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {room.location.station}駅 徒歩{room.location.walkingMinutes}分
                    </Typography>
                    {room.roomDetails.amenities && room.roomDetails.amenities.length > 0 && (
                        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {room.roomDetails.amenities.slice(0, 3).map((amenity) => (
                                <Chip
                                    key={amenity}
                                    label={AMENITY_LABELS[amenity]}
                                    size="small"
                                    variant="outlined"
                                />
                            ))}
                            {room.roomDetails.amenities.length > 3 && (
                                <Chip
                                    label={`+${room.roomDetails.amenities.length - 3}`}
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                        </Box>
                    )}
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                            onClick={handleEdit}
                            startIcon={<EditIcon />}
                            color="primary"
                        >
                            編集
                        </Button>
                        <Button
                            onClick={handleDeleteClick}
                            startIcon={<DeleteIcon />}
                            color="error"
                        >
                            削除
                        </Button>
                    </Box>
                </CardContent>
                <CardActions onClick={(e) => e.stopPropagation()}>
                    <Button
                        size="small"
                        onClick={handleStatusToggle}
                        variant="outlined"
                        color={room.status === 'active' ? 'secondary' : 'primary'}
                    >
                        {getStatusButtonLabel()}
                    </Button>
                </CardActions>
            </Card>

            <Dialog
                open={openDeleteDialog}
                onClose={() => setOpenDeleteDialog(false)}
                onClick={(e) => e.stopPropagation()}
            >
                <DialogTitle>物件の削除</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        この物件を削除してもよろしいですか？
                        この操作は取り消すことができません。
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>
                        キャンセル
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        variant="contained"
                    >
                        削除する
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default RoomManageCard; 